import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer } from '@aimsos/ui';

import { iconForRecordType } from '../../utils/recordTypeIcons.js';

const Choice = memo(function Choice({ id, icon: Icon, label, description, selected, onSelect }) {
  const handleClick = useCallback(() => onSelect(id), [onSelect, id]);

  return (
    <CartContainer
      role="radio"
      aria-checked={selected}
      selected={selected}
      variant={selected ? 'primary' : 'neutral'}
      onClick={handleClick}
      className="flex-1"
    >
      <span className="flex items-start gap-3">
        <Icon className="mt-0.5 size-5 shrink-0 text-content-secondary" aria-hidden />
        <span className="flex flex-col gap-0.5 text-left">
          <span className="text-sm font-semibold text-content">{label}</span>
          <span className="text-xs text-content-muted">{description}</span>
        </span>
      </span>
    </CartContainer>
  );
});

/**
 * Which kind of record is being created. A radiogroup of cards: choosing a type
 * is a decision about the whole form, so it reads as a choice, not a field.
 */
function RecordTypeChoice({ types, selectedId, onSelect, labelledBy }) {
  const { t } = useTranslation('unified-profiles');

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : t('create.type.legend')}
      className="flex flex-col gap-3 sm:flex-row"
    >
      {types.map((id) => (
        <Choice
          key={id}
          id={id}
          icon={iconForRecordType(id)}
          label={t(`create.type.${id}.label`)}
          description={t(`create.type.${id}.description`)}
          selected={id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

export default memo(RecordTypeChoice);
